import type {
  BulkRetryWebhookDeliveriesRequest,
  ListWebhookDeliveriesRequest,
} from '@compass/client/types/webhook';
import { webhookService } from '@services/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/services/query.config';

/**
 * Delivery queries and mutations. Deliveries live in the dispatcher, so these
 * fail with 503 when it is down; callers render that inline (never a crash)
 * while the Endpoints tab keeps working.
 */

/**
 * `pollMs` is Live mode: re-read on that interval while the browser tab is
 * visible (react-query skips background tabs); `false` reads on demand only.
 */
export function useWebhookDeliveries(
  request: ListWebhookDeliveriesRequest,
  enabled: boolean,
  pollMs: number | false = false,
) {
  const organizationId = request.organization_id ?? '';
  // The cursor is part of the request; key the filters without it.
  const { start, ...filters } = request;
  return useQuery({
    queryKey: queryKeys.webhooks.deliveries(organizationId, filters, start),
    queryFn: () => webhookService.listWebhookDeliveries(request),
    enabled: enabled && !!organizationId,
    staleTime: 15 * 1000,
    refetchInterval: pollMs,
    // Keep the current page on screen while the next one loads — but never
    // another organization's page (queryKey[2] is the org id).
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === organizationId ? previous : undefined,
    throwOnError: false,
  });
}

export function useWebhookDelivery(
  organizationId: string,
  deliveryId: string,
  enabled: boolean,
  pollMs: number | false = false,
) {
  return useQuery({
    refetchInterval: pollMs,
    queryKey: queryKeys.webhooks.delivery(organizationId, deliveryId),
    // The response wraps the detail ({detail}); React Query rejects undefined data.
    queryFn: async () =>
      (
        await webhookService.getWebhookDelivery({
          organization_id: organizationId,
          delivery_id: deliveryId,
        })
      ).detail ?? null,
    enabled: enabled && !!organizationId && !!deliveryId,
    staleTime: 10 * 1000,
    throwOnError: false,
  });
}

function useInvalidateWebhooks(organizationId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.org(organizationId) });
}

/** Retry now (retrying/exhausted) and Resend (succeeded) are the same RPC. */
export function useRetryWebhookDelivery(organizationId: string) {
  const invalidate = useInvalidateWebhooks(organizationId);
  return useMutation({
    mutationFn: async (deliveryId: string) =>
      (
        await webhookService.retryWebhookDelivery({
          organization_id: organizationId,
          delivery_id: deliveryId,
        })
      ).delivery,
    onSettled: invalidate,
  });
}

export function useBulkRetryWebhookDeliveries(organizationId: string) {
  const invalidate = useInvalidateWebhooks(organizationId);
  return useMutation({
    mutationFn: async (request: BulkRetryWebhookDeliveriesRequest) =>
      (await webhookService.bulkRetryWebhookDeliveries(request)).operation,
    onSettled: invalidate,
  });
}
